import { useMemo, useState } from 'react';
import { usePOS } from '@/contexts/POSContext';
import { formatFiat, formatBCH } from '@/lib/bch';
import { Transaction, loadTransactions } from '@/lib/posStorage';
import { cn } from '@/lib/utils';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

// ─── Period helpers ────────────────────────────────────────────────────────────

type Period = '7d' | '30d' | '90d' | 'all';

const PERIODS: { id: Period; label: string }[] = [
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: '90d', label: '90 days' },
  { id: 'all', label: 'All time' },
];

function periodStartMs(period: Period): number {
  if (period === 'all') return 0;
  const days = period === '7d' ? 7 : period === '30d' ? 30 : 90;
  return Date.now() - days * 86400_000;
}

function filterByPeriod(txs: Transaction[], period: Period): Transaction[] {
  const start = periodStartMs(period);
  return txs.filter((tx) => tx.timestamp >= start && !tx.refunded);
}

// ─── Chart data builders ──────────────────────────────────────────────────────

function buildRevenueByDay(txs: Transaction[], period: Period, currency: string) {
  if (txs.length === 0) return [];

  const days = period === '7d' ? 7 : period === '30d' ? 30 : period === '90d' ? 90 : 0;
  const startDate = days > 0
    ? new Date(Date.now() - days * 86400_000)
    : new Date(Math.min(...txs.map((t) => t.timestamp)));

  const map = new Map<string, { fiat: number; bch: number; count: number }>();

  // Pre-fill all dates in range so chart has no gaps
  const cursor = new Date(startDate);
  cursor.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  while (cursor <= today) {
    const key = cursor.toISOString().slice(0, 10);
    map.set(key, { fiat: 0, bch: 0, count: 0 });
    cursor.setDate(cursor.getDate() + 1);
  }

  for (const tx of txs) {
    const key = new Date(tx.timestamp).toISOString().slice(0, 10);
    const existing = map.get(key) ?? { fiat: 0, bch: 0, count: 0 };
    existing.fiat += tx.fiatAmount;
    existing.bch += tx.bchAmount;
    existing.count += 1;
    map.set(key, existing);
  }

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, data]) => ({
      date,
      label: new Date(date + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      revenue: parseFloat(data.fiat.toFixed(2)),
      bch: parseFloat(data.bch.toFixed(8)),
      sales: data.count,
      currency,
    }));
}

function buildSalesByHour(txs: Transaction[]) {
  const hours = Array.from({ length: 24 }, (_, i) => ({
    hour: i,
    label: i === 0 ? '12a' : i < 12 ? `${i}a` : i === 12 ? '12p' : `${i - 12}p`,
    sales: 0,
    revenue: 0,
  }));

  for (const tx of txs) {
    const h = new Date(tx.timestamp).getHours();
    hours[h].sales += 1;
    hours[h].revenue += tx.fiatAmount;
  }

  return hours;
}

function buildTopProducts(txs: Transaction[], limit = 5) {
  const map = new Map<string, { name: string; qty: number; revenue: number }>();

  for (const tx of txs) {
    if (!tx.items) continue;
    for (const item of tx.items) {
      const existing = map.get(item.name) ?? { name: item.name, qty: 0, revenue: 0 };
      existing.qty += item.qty;
      existing.revenue += item.price * item.qty;
      map.set(item.name, existing);
    }
  }

  return Array.from(map.values())
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, limit);
}

function buildTipStats(txs: Transaction[]) {
  const tipped = txs.filter((tx) => (tx.tipAmount ?? 0) > 0);
  const totalTips = tipped.reduce((sum, tx) => sum + (tx.tipAmount ?? 0), 0);
  const avgTip = tipped.length > 0 ? totalTips / tipped.length : 0;
  const tipRate = txs.length > 0 ? (tipped.length / txs.length) * 100 : 0;
  return { totalTips, avgTip, tipRate, tippedCount: tipped.length, totalCount: txs.length };
}

// ─── Custom tooltip ──────────────────────────────────────────────────────────

interface TooltipPayloadEntry {
  value: number;
  name: string;
  payload: Record<string, unknown>;
}

function RevenueTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload as { revenue: number; bch: number; sales: number; currency: string };
  return (
    <div className="bg-zinc-900 text-white rounded-xl px-3 py-2 shadow-lg text-xs space-y-0.5">
      <p className="font-semibold">{label}</p>
      <p>{formatFiat(data.revenue, data.currency as string)}</p>
      <p className="text-[#0AC18E]">{formatBCH(data.bch)} BCH</p>
      <p className="text-zinc-400">{data.sales} sale{data.sales !== 1 ? 's' : ''}</p>
    </div>
  );
}

function HourTooltip({ active, payload }: {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
}) {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload as { label: string; sales: number; revenue: number };
  return (
    <div className="bg-zinc-900 text-white rounded-xl px-3 py-2 shadow-lg text-xs space-y-0.5">
      <p className="font-semibold">{data.label}</p>
      <p>{data.sales} sale{data.sales !== 1 ? 's' : ''}</p>
    </div>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent }: {
  label: string; value: string; sub?: string; accent?: boolean;
}) {
  return (
    <div className={cn(
      'rounded-2xl p-4 border',
      accent
        ? 'bg-[#0AC18E]/10 dark:bg-[#0AC18E]/15 border-[#0AC18E]/20'
        : 'bg-white dark:bg-zinc-800 border-zinc-100 dark:border-zinc-700'
    )}>
      <p className={cn('text-xs font-semibold uppercase tracking-wider mb-1', accent ? 'text-[#0AC18E]' : 'text-zinc-400')}>
        {label}
      </p>
      <p className="text-xl font-black text-zinc-900 dark:text-zinc-50">{value}</p>
      {sub && <p className="text-xs text-zinc-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────

export function DashboardView() {
  const { settings, isPro, setView } = usePOS();
  const [period, setPeriod] = useState<Period>('30d');

  const allTxs = useMemo(() => loadTransactions(), []);
  const txs = useMemo(() => filterByPeriod(allTxs, period), [allTxs, period]);

  const revenueData = useMemo(() => buildRevenueByDay(txs, period, settings.currency), [txs, period, settings.currency]);
  const hourData = useMemo(() => buildSalesByHour(txs), [txs]);
  const topProducts = useMemo(() => buildTopProducts(txs), [txs]);
  const tipStats = useMemo(() => buildTipStats(txs), [txs]);

  const totalRevenue = txs.reduce((s, tx) => s + tx.fiatAmount, 0);
  const totalBCH = txs.reduce((s, tx) => s + tx.bchAmount, 0);
  const avgSale = txs.length > 0 ? totalRevenue / txs.length : 0;
  const totalDiscount = txs.reduce((s, tx) => s + (tx.discountAmount ?? 0), 0);

  // Pro gate
  if (!isPro) {
    return (
      <div className="flex flex-col gap-5 pb-6">
        <div className="text-center pt-2">
          <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-50">Dashboard</h2>
          <p className="text-sm text-zinc-400">Sales analytics &amp; insights</p>
        </div>

        {/* Blurred preview teaser */}
        <div className="relative rounded-3xl overflow-hidden">
          <div className="blur-sm pointer-events-none select-none opacity-60">
            <div className="bg-white dark:bg-zinc-800 rounded-2xl p-4 border border-zinc-100 dark:border-zinc-700">
              <div className="h-40 bg-gradient-to-t from-[#0AC18E]/20 to-transparent rounded-xl" />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="bg-white dark:bg-zinc-800 rounded-2xl p-4 border border-zinc-100 dark:border-zinc-700">
                <div className="h-4 w-16 bg-zinc-200 dark:bg-zinc-700 rounded mb-2" />
                <div className="h-6 w-24 bg-zinc-200 dark:bg-zinc-700 rounded" />
              </div>
              <div className="bg-white dark:bg-zinc-800 rounded-2xl p-4 border border-zinc-100 dark:border-zinc-700">
                <div className="h-4 w-16 bg-zinc-200 dark:bg-zinc-700 rounded mb-2" />
                <div className="h-6 w-24 bg-zinc-200 dark:bg-zinc-700 rounded" />
              </div>
            </div>
          </div>
          {/* Overlay CTA */}
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white/60 dark:bg-zinc-950/60 backdrop-blur-[2px]">
            <div className="text-5xl">📊</div>
            <h3 className="text-lg font-black text-zinc-900 dark:text-zinc-50">Unlock Analytics</h3>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 text-center max-w-xs px-4">
              Revenue charts, peak hours, top products, and tip insights — all in one dashboard.
            </p>
            <button
              onClick={() => setView('upgrade')}
              className="px-6 py-3 rounded-xl bg-[#0AC18E] text-white font-bold text-sm shadow-lg shadow-[#0AC18E]/30 hover:bg-[#09AF81] active:scale-95 transition-all"
            >
              Upgrade to Pro
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (allTxs.length === 0) {
    return (
      <div className="flex flex-col gap-5 pb-6">
        <div className="text-center pt-2">
          <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-50">Dashboard</h2>
          <p className="text-sm text-zinc-400">Sales analytics &amp; insights</p>
        </div>
        <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-zinc-200 dark:border-zinc-700 rounded-3xl">
          <div className="text-5xl mb-4">📊</div>
          <p className="text-zinc-700 dark:text-zinc-200 font-semibold text-base mb-1">No data yet</p>
          <p className="text-sm text-zinc-400 dark:text-zinc-500 max-w-xs">
            Complete your first sale and your analytics will appear here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 pb-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-50">Dashboard</h2>
          <p className="text-sm text-zinc-400">{txs.length} sales in period</p>
        </div>
        <span className="text-xs font-bold text-[#0AC18E] bg-[#0AC18E]/10 dark:bg-[#0AC18E]/20 px-2.5 py-1 rounded-full border border-[#0AC18E]/20">
          Pro
        </span>
      </div>

      {/* Period selector */}
      <div className="flex bg-zinc-100 dark:bg-zinc-800 rounded-2xl p-1">
        {PERIODS.map((p) => (
          <button
            key={p.id}
            onClick={() => setPeriod(p.id)}
            className={cn(
              'flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all',
              period === p.id
                ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-50 shadow-sm'
                : 'text-zinc-400 dark:text-zinc-500'
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard
          label="Revenue"
          value={formatFiat(totalRevenue, settings.currency)}
          sub={`${formatBCH(totalBCH)} BCH`}
          accent
        />
        <StatCard
          label="Sales"
          value={txs.length.toString()}
          sub={`Avg ${formatFiat(avgSale, settings.currency)}`}
        />
        <StatCard
          label="Tips"
          value={formatFiat(tipStats.totalTips, settings.currency)}
          sub={`${tipStats.tipRate.toFixed(0)}% tip rate`}
        />
        <StatCard
          label="Discounts"
          value={totalDiscount > 0 ? `-${formatFiat(totalDiscount, settings.currency)}` : formatFiat(0, settings.currency)}
          sub={`Given to customers`}
        />
      </div>

      {/* Revenue over time chart */}
      {revenueData.length > 1 && (
        <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-100 dark:border-zinc-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-100 dark:border-zinc-700">
            <p className="text-sm font-bold text-zinc-800 dark:text-zinc-100">Revenue Over Time</p>
          </div>
          <div className="px-2 pt-4 pb-2">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0AC18E" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#0AC18E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-zinc-200, #e4e4e7)" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: '#a1a1aa' }}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 10, fill: '#a1a1aa' }}
                  tickLine={false}
                  axisLine={false}
                  width={50}
                  tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toString()}
                />
                <Tooltip content={<RevenueTooltip />} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#0AC18E"
                  strokeWidth={2}
                  fill="url(#revenueGrad)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Sales by hour */}
      {hourData.some((h) => h.sales > 0) && (
        <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-100 dark:border-zinc-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-100 dark:border-zinc-700">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-zinc-800 dark:text-zinc-100">Peak Hours</p>
              <p className="text-xs text-zinc-400">
                Busiest: {(() => {
                  const max = Math.max(...hourData.map((h) => h.sales));
                  const peak = hourData.find((h) => h.sales === max);
                  return peak?.label ?? '—';
                })()}
              </p>
            </div>
          </div>
          <div className="px-2 pt-4 pb-2">
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={hourData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-zinc-200, #e4e4e7)" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 9, fill: '#a1a1aa' }}
                  tickLine={false}
                  axisLine={false}
                  interval={2}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: '#a1a1aa' }}
                  tickLine={false}
                  axisLine={false}
                  width={30}
                  allowDecimals={false}
                />
                <Tooltip content={<HourTooltip />} />
                <Bar dataKey="sales" fill="#0AC18E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Top products */}
      {topProducts.length > 0 && (
        <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-100 dark:border-zinc-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-100 dark:border-zinc-700">
            <p className="text-sm font-bold text-zinc-800 dark:text-zinc-100">Top Products</p>
          </div>
          <div className="divide-y divide-zinc-100 dark:divide-zinc-700">
            {topProducts.map((product, i) => {
              const maxRevenue = topProducts[0].revenue;
              const pct = maxRevenue > 0 ? (product.revenue / maxRevenue) * 100 : 0;
              return (
                <div key={product.name} className="px-4 py-3 flex items-center gap-3">
                  <span className={cn(
                    'w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs font-black',
                    i === 0
                      ? 'bg-[#0AC18E] text-white'
                      : 'bg-zinc-100 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-400'
                  )}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate">
                      {product.name}
                    </p>
                    <div className="w-full bg-zinc-100 dark:bg-zinc-700 rounded-full h-1.5 mt-1.5">
                      <div
                        className="bg-[#0AC18E] h-1.5 rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-zinc-800 dark:text-zinc-100">
                      {formatFiat(product.revenue, settings.currency)}
                    </p>
                    <p className="text-xs text-zinc-400">{product.qty} sold</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tip insights */}
      {tipStats.tippedCount > 0 && (
        <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-100 dark:border-zinc-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-100 dark:border-zinc-700 flex items-center gap-2">
            <span className="text-base">💰</span>
            <p className="text-sm font-bold text-zinc-800 dark:text-zinc-100">Tip Insights</p>
          </div>
          <div className="px-4 py-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center">
                <p className="text-lg font-black text-[#0AC18E]">
                  {formatFiat(tipStats.totalTips, settings.currency)}
                </p>
                <p className="text-xs text-zinc-400 mt-0.5">Total tips</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-black text-zinc-900 dark:text-zinc-50">
                  {formatFiat(tipStats.avgTip, settings.currency)}
                </p>
                <p className="text-xs text-zinc-400 mt-0.5">Avg tip</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-black text-zinc-900 dark:text-zinc-50">
                  {tipStats.tipRate.toFixed(0)}%
                </p>
                <p className="text-xs text-zinc-400 mt-0.5">Tip rate</p>
              </div>
            </div>
            <p className="text-xs text-zinc-400 text-center mt-3">
              {tipStats.tippedCount} of {tipStats.totalCount} sales included a tip
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
